/**
 * 设备状态管理 Store
 * 使用 Pinia 进行状态管理
 *
 * 职责:
 * - 管理 HID 设备的连接状态
 * - 缓存设备详情和状态数据
 * - 提供命令发送接口
 * - 处理轮询和事件监听
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import type { DeviceDetail, DeviceStatus, ConnectionType, DeviceCapabilities } from '@/api/device/types'
import { DEFAULT_DEVICE_CAPABILITIES, DEVICE_CAPABILITY_MAP } from '@/api/device/types'
import {
  buildSpeakerMuteCommand,
  buildMicMuteCommand,
  buildSpeakerVolumeCommand,
  buildMicVolumeCommand,
  buildEncCommand,
  buildRgbCommand,
  buildPowerCommand,
  buildAudioSplitCommand,
  buildAudioBalanceCommand,
} from '@/api/device/commands'

export const useDeviceStore = defineStore('device', () => {
  // State
  // isConnected: 仅表示 HID 通道已打开，不等于耳机链路已就绪
  const isConnected = ref(false)
  // isDeviceConnected: 业务链路是否真正连通（用于顶部状态展示/功能可用性）
  const isDeviceConnected = ref(false)
  const isConnecting = ref(false)
  const deviceDetail = ref<DeviceDetail | null>(null)
  const deviceStatus = ref<DeviceStatus | null>(null)
  const connectionType = ref<ConnectionType>(null)
  const error = ref<string | null>(null)
  const connectionStage = ref<string>('idle')
  const connectionMessage = ref<string | null>(null)
  const isRefreshing = ref(false) // Prevent concurrent refreshStatus calls

  // 降噪设置（1-6档，对齐旧版 server-1.js）
  const noiseEnabled = ref(false)
  const noiseLevel = ref(1) // 默认1档，范围1-6
  const lastNoiseLevel = ref(1) // 记录关闭前的档位

  // 静音状态
  const speakerMuted = ref(false)
  const micMuted = ref(false)

  // 音量设置 (0-100)
  const speakerVolume = ref(50)
  const micVolume = ref(50)

  // RGB设置
  const rgbEnabled = ref(false)
  const rgbMode = ref(0)
  const rgbBrightness = ref(50)
  const lastRgbMode = ref(1)

  // 音频混合/分离
  const audioSplitEnabled = ref(false)
  const audioBalanceValue = ref(50) // 0-100, 50=中间

  // Getters
  const deviceCategory = computed(() => {
    const fromDetail = deviceDetail.value?.deviceCategory
    if (fromDetail && fromDetail !== 'unknown') return fromDetail

    // Legacy alignment: original project uses either deviceType (from detail)
    // OR status.productType to branch UI (0=headPhone, 1=microPhone)
    const pt = deviceStatus.value?.productType
    if (typeof pt === 'number') {
      if (pt === 1) return 'microPhone'
      return 'headPhone'
    }

    return fromDetail || 'unknown'
  })
  const isHeadPhone = computed(() => deviceCategory.value === 'headPhone')
  const isMicroPhone = computed(() => deviceCategory.value === 'microPhone')
  const batteryLevel = computed(() => deviceStatus.value?.batteryLevel ?? 0)
  // 对齐旧项目 globalVariable.js updateChargingStatus：batteryCharging===1 或 linkStatus===1 均显示充电图标
  const isCharging = computed(() => {
    const s = deviceStatus.value
    return !!(s && (s.batteryCharging === 1 || s.linkStatus === 1))
  })
  const isPowerOff = computed(() => (deviceStatus.value?.powerOffStatus || 0) === 1)

  // 设备能力配置（支持扩展新设备类型）
  const deviceCapabilities = computed<DeviceCapabilities>(() => {
    const category = deviceCategory.value
    const specificCaps = DEVICE_CAPABILITY_MAP[category] || {}
    return { ...DEFAULT_DEVICE_CAPABILITIES, ...specificCaps }
  })

  // 便捷的能力检查 getter
  const canUseEq = computed(() => deviceCapabilities.value.hasEq)
  const canUseNoiseReduction = computed(() => deviceCapabilities.value.hasNoiseReduction)
  const canUseSpecialAudio = computed(() => deviceCapabilities.value.hasSpecialAudio)
  const canUseLed = computed(() => deviceCapabilities.value.hasLed)
  const canUseHotkey = computed(() => deviceCapabilities.value.hasHotkey)
  const canUseLanguageSwitch = computed(() => deviceCapabilities.value.hasLanguageSwitch)
  const canUseFirmwareUpgrade = computed(() => deviceCapabilities.value.hasFirmwareUpgrade)
  const canUsePowerControl = computed(() => deviceCapabilities.value.hasPowerControl)
  const canUseVoiceChange = computed(() => deviceCapabilities.value.hasVoiceChange)

  // Actions

  function syncDeviceStatus(status: DeviceStatus): void {
    deviceStatus.value = status
    connectionType.value = hidManager.getConnectionType()

    noiseEnabled.value = (status.noiseReductionEnabled || 0) === 1
    noiseLevel.value = status.noiseReductionMode || 1
    if (!noiseEnabled.value) lastNoiseLevel.value = noiseLevel.value

    speakerMuted.value = (status.speakerMute || 0) !== 0
    micMuted.value = (status.micMute || 0) !== 0
    speakerVolume.value = Math.max(0, Math.min(100, status.speakerVolumeLevel || 0))
    micVolume.value = Math.max(0, Math.min(100, status.micVolumeLevel || 0))

    rgbMode.value = status.ledMode || 0
    rgbEnabled.value = rgbMode.value !== 0
    if (rgbMode.value !== 0) lastRgbMode.value = rgbMode.value

    audioSplitEnabled.value = Number(status.audioChannelMode || 0) === 1
    audioBalanceValue.value = Math.max(0, Math.min(100, Math.round((status.mixVolume || 0) / 2)))

    // 0x80 里有固件版本，旧项目会合并到 deviceDetail 供固件升级页使用
    if (deviceDetail.value) {
      deviceDetail.value = {
        ...deviceDetail.value,
        softwareVersion: status.softwareVersion || deviceDetail.value.softwareVersion || '',
        bluetoothVersion: status.bluetoothVersion || deviceDetail.value.bluetoothVersion || '',
        noiseCancelVersion: status.noiseCancelVersion || deviceDetail.value.noiseCancelVersion || '',
        dongleVersion: status.dongleVersion || deviceDetail.value.dongleVersion || '',
      }
    }
  }

  async function connect(): Promise<boolean> {
    if (isConnecting.value) return false

    isConnecting.value = true
    error.value = null

    try {
      const success = await hidManager.connect()
      if (!success) {
        error.value = 'Failed to connect to device'
      }
      return success
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Connection error'
      return false
    } finally {
      isConnecting.value = false
    }
  }

  async function disconnect(): Promise<void> {
    await hidManager.disconnect()
  }

  // 轮询状态：只做"提升为已连接"，不做"降级为未连接"
  // 真实断开统一由 HID disconnect 事件驱动，避免瞬时抖动导致 UI 反复切换
  async function refreshStatus(): Promise<void> {
    if (!isConnected.value || isRefreshing.value) return

    isRefreshing.value = true
    try {
      const status = await hidManager.getDeviceStatus()
      if (status) {
        syncDeviceStatus(status)
        if (connectionType.value === '2.4G' && status.headsetConnected === 1) isDeviceConnected.value = true
        if (connectionType.value === 'USB' && status.linkStatus === 1) isDeviceConnected.value = true
      }
    } finally {
      isRefreshing.value = false
    }
  }

  // Initialize auto-detection with auto-connect on startup
  async function initAutoDetection(): Promise<boolean> {
    // Wiring events once
    hidManager.on('device-connected', (detail: DeviceDetail) => {
      console.log('[DeviceStore] device-connected event:', detail.deviceName)
      deviceDetail.value = detail
      isConnected.value = true
      // 不在 0x82 详情返回时进入主界面；必须等待 0x80 状态确认业务连接
      error.value = null
    })

    hidManager.on('status-updated', (status: DeviceStatus) => {
      syncDeviceStatus(status)
    })

    hidManager.on('device-disconnected', () => {
      console.log('[DeviceStore] device-disconnected event')
      isConnected.value = false
      isDeviceConnected.value = false
      deviceDetail.value = null
      deviceStatus.value = null
      connectionType.value = null
      error.value = null
    })

    // Note: data-received no longer triggers immediate refreshStatus
    // to avoid conflicts with connection flow. Polling handles periodic updates.

    // connection-stage 是底层 HID 状态机对 UI 的桥接层
    hidManager.on('connection-stage', payload => {
      console.log('[DeviceStore] connection-stage:', payload.stage, payload.message)
      connectionStage.value = payload.stage
      connectionMessage.value = payload.message || null
      if (payload.connectionType) {
        connectionType.value = payload.connectionType
      }
      // 打开设备后就进入主界面容器（不再显示等待遮罩）
      if (payload.stage === 'opening_device' || payload.stage === 'waiting_for_pairing') {
        isConnected.value = true
      }
      // 业务链路确认成功后，再标记为 fully connected
      if (payload.stage === 'connected') {
        isConnected.value = true
        isDeviceConnected.value = true
      }
      if (payload.stage === 'disconnected') {
        isConnected.value = false
        isDeviceConnected.value = false
      }
    })

    hidManager.on('error', (err: Error) => {
      console.log('[DeviceStore] error:', err.message)
      error.value = err.message
    })

    const autoConnected = await hidManager.init()

    // Note: Polling is now started only after device-connected event
    // to avoid conflicts with the connection process

    return autoConnected
  }

  // 轮询已移除：连接成功后 fetchDeviceStatusAndDetail 完成一次全量抓取并缓存，
  // 之后由 HID 实时帧（inputreport）驱动增量更新，断开时由 disconnect 事件清理。
  // 不再周期性发 HID 命令，避免页面加载时 3-5s 阻塞等待响应。
  function startStatusPolling(): void { /* no-op: removed periodic polling */ }
  function stopStatusPolling(): void { /* no-op */ }

  // 降噪设置方法
  // ============ Command Sending Actions ============

  /**
   * 发送原始数据到设备
   */
  async function sendRawData(data: Uint8Array | string, waitForResponse = false): Promise<Uint8Array | void> {
    if (!isConnected.value) {
      throw new Error('Device not connected')
    }
    return hidManager.sendData(data, waitForResponse)
  }

  /**
   * 设置喇叭静音
   */
  async function setSpeakerMute(mute: boolean): Promise<void> {
    speakerMuted.value = mute
    const command = buildSpeakerMuteCommand(mute)
    await sendRawData(command, false)
  }

  /**
   * 设置麦克风静音
   */
  async function setMicMute(mute: boolean): Promise<void> {
    micMuted.value = mute
    const command = buildMicMuteCommand(mute)
    await sendRawData(command, false)
  }

  /**
   * 设置喇叭音量 (0-100)
   */
  async function setSpeakerVolume(volume: number): Promise<void> {
    const prev = speakerVolume.value
    const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
    speakerVolume.value = clampedVolume
    const command = buildSpeakerVolumeCommand(clampedVolume)
    await sendRawData(command, false)

    // 对齐旧版: 音量滑到0自动静音；从0增大自动取消静音
    if (clampedVolume === 0 && !speakerMuted.value) {
      await setSpeakerMute(true)
    } else if (clampedVolume > 0 && prev === 0 && speakerMuted.value) {
      await setSpeakerMute(false)
    }
  }

  /**
   * 设置麦克风音量 (0-100)
   */
  async function setMicVolume(volume: number): Promise<void> {
    const prev = micVolume.value
    const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
    micVolume.value = clampedVolume
    const command = buildMicVolumeCommand(clampedVolume)
    await sendRawData(command, false)

    // 对齐旧版: 麦克风音量滑到0自动静音；从0增大自动取消静音
    if (clampedVolume === 0 && !micMuted.value) {
      await setMicMute(true)
    } else if (clampedVolume > 0 && prev === 0 && micMuted.value) {
      await setMicMute(false)
    }
  }

  /**
   * 设置ENC降噪
   * 对齐旧版: 档位1-6，关闭时记录lastValue，开启时恢复
   */
  async function setNoiseEnabled(enabled: boolean): Promise<void> {
    if (enabled) {
      // 开启时恢复上次档位
      const restoreLevel = lastNoiseLevel.value || 1
      noiseEnabled.value = true
      noiseLevel.value = restoreLevel
    } else {
      // 关闭时记录当前档位
      lastNoiseLevel.value = noiseLevel.value
      noiseEnabled.value = false
    }

    const command = buildEncCommand(noiseEnabled.value, noiseLevel.value)
    await sendRawData(command, false)
  }

  async function setNoiseLevel(level: number): Promise<void> {
    const clampedLevel = Math.max(1, Math.min(6, Math.round(level)))
    noiseLevel.value = clampedLevel

    // 如果当前降噪开启，发送命令；否则只更新值
    if (noiseEnabled.value) {
      const command = buildEncCommand(true, clampedLevel)
      await sendRawData(command, false)
    }
  }

  /**
   * 设置RGB灯效
   */
  async function setRgbEnabled(enabled: boolean): Promise<void> {
    if (!enabled) {
      if (rgbMode.value !== 0) {
        lastRgbMode.value = rgbMode.value
      }
      rgbEnabled.value = false
      rgbMode.value = 0
      const command = buildRgbCommand(false, 0)
      await sendRawData(command, false)
      return
    }

    rgbEnabled.value = true
    if (rgbMode.value === 0) {
      rgbMode.value = lastRgbMode.value || 1
    }
    const command = buildRgbCommand(true, rgbMode.value)
    await sendRawData(command, false)
  }

  async function setRgbMode(mode: number): Promise<void> {
    rgbMode.value = mode
    if (rgbEnabled.value) {
      const command = buildRgbCommand(true, mode)
      await sendRawData(command, false)
    }
  }

  /**
   * 对齐旧版: 仅 2.4G 模式支持关机/开机 (cmd 0x70)
   */
  async function setPowerOn(powerOn: boolean): Promise<void> {
    if (!canUsePowerControl.value) {
      return
    }
    const command = buildPowerCommand(powerOn)
    await sendRawData(command, false)
  }

  async function togglePower(): Promise<void> {
    // 旧版: powerOffStatus=1 表示软件关机
    await setPowerOn(isPowerOff.value)
  }

  /**
   * 设置音频分离/混合
   */
  async function setAudioSplit(enabled: boolean): Promise<void> {
    audioSplitEnabled.value = enabled
    const command = buildAudioSplitCommand(enabled)
    await sendRawData(command, false)
  }

  /**
   * 设置音频平衡 (0-100)
   */
  async function setAudioBalance(value: number): Promise<void> {
    const clampedValue = Math.max(0, Math.min(100, Math.round(value)))
    audioBalanceValue.value = clampedValue
    const command = buildAudioBalanceCommand(clampedValue)
    await sendRawData(command, false)
  }

  // ============ Legacy Compatibility ============

  /**
   * 兼容旧版: 切换喇叭静音状态
   */
  async function toggleSpeakerMute(): Promise<void> {
    await setSpeakerMute(!speakerMuted.value)
  }

  /**
   * 兼容旧版: 切换麦克风静音状态
   */
  async function toggleMicMute(): Promise<void> {
    await setMicMute(!micMuted.value)
  }

  /** 兼容 AI 助手：喇叭静音 */
  async function setMute(mute: boolean): Promise<void> {
    await setSpeakerMute(mute)
  }

  return {
    // State
    isConnected,
    isDeviceConnected,
    isConnecting,
    deviceDetail,
    deviceStatus,
    connectionType,
    connectionStage,
    connectionMessage,
    error,
    noiseEnabled,
    noiseLevel,
    lastNoiseLevel,
    speakerMuted,
    micMuted,
    speakerVolume,
    micVolume,
    rgbEnabled,
    rgbMode,
    rgbBrightness,
    lastRgbMode,
    audioSplitEnabled,
    audioBalanceValue,
    // Getters
    deviceCategory,
    isHeadPhone,
    isMicroPhone,
    batteryLevel,
    isCharging,
    isPowerOff,
    // 设备能力（支持扩展新设备类型）
    deviceCapabilities,
    canUseEq,
    canUseNoiseReduction,
    canUseSpecialAudio,
    canUseLed,
    canUseHotkey,
    canUseLanguageSwitch,
    canUseFirmwareUpgrade,
    canUsePowerControl,
    canUseVoiceChange,
    // Actions
    connect,
    disconnect,
    refreshStatus,
    initAutoDetection,
    startStatusPolling,
    stopStatusPolling,
    setNoiseEnabled,
    setNoiseLevel,
    setSpeakerMute,
    setMicMute,
    setSpeakerVolume,
    setMicVolume,
    setRgbEnabled,
    setRgbMode,
    togglePower,
    setPowerOn,
    setAudioSplit,
    setAudioBalance,
    toggleSpeakerMute,
    toggleMicMute,
    setMute,
    sendRawData,
  }
})
